<template>
  <div class="ag-cell-name-renderer" data-test-id="grid-name-renderer">
    <div v-if="isFolder" class="ag-cell-name-renderer__group-icon">
      <FolderFilledIcon16 />
    </div>

    <div
      class="ag-cell-name-renderer__wrapper"
      :class="{ 'ag-cell-name-renderer__wrapper--name-editing': isEditModeActive }"
    >
      <div
        class="ag-cell-name-renderer__name"
        :class="{
          'ag-cell-name-renderer__name--is-editable': params.isEditable,
        }"
        :style="{ maxWidth: `calc(100% - ${rightInputPadding}px)` }"
      >
        <div v-if="isEditModeActive" @keyup.esc="cancelEditing">
          <ProsemirrorInputBox
            v-if="isColumnWrapped"
            v-model="inputValue"
            @blur="handleBlur"
            @focus="triggerHideActionButtons"
            focus-on-mount
            blur-on-enter
          />
          <InputBox
            v-else
            v-model="inputValue"
            test-id="ag-cell-name-renderer_input-field"
            @blur="handleBlur"
            @dblclick="stopEventPropagation"
            focus-on-mount
            blur-on-enter-press
          />
        </div>
        <div v-else @click="toggleEditMode">
          <template v-if="isHighlightedContent">
            <template v-for="content in highlightContent">
              <span
                :key="`${content.from}-formatted`"
                v-if="content.format"
                data-test-highlighted
                :class="{
                  'ag-cell-name-renderer__highlight': true,
                  'ag-cell-name-renderer__wrapped-column': isColumnWrapped,
                }"
                >{{ nameString.substring(content.from, content.to) }}</span
              >
              <span :key="content.from" v-else>{{ nameString.substring(content.from, content.to) }}</span>
            </template>
          </template>
          <template v-else>
            <span
              :class="{
                'ag-cell-name-renderer__wrapped-column': isColumnWrapped,
                'ag-cell-name-renderer__placeholder': !hasName,
              }"
            >
              {{ params.value || namePlaceholder }}
            </span>
          </template>
        </div>
        <transition
          v-if="!isColumnWrapped && (params.value !== '' || (isHighlightedContent && highlightContent.length > 0))"
          name="fade-transition"
        >
          <span v-if="!isEditModeActive" class="ag-cell__gradient"></span>
        </transition>
      </div>

      <div v-if="hasChildren" class="ag-cell-name-renderer__child-count">
        <component :is="childrenIconComponent" />
        <span data-test-id="grid-name__child-count">{{ childCount }}</span>
      </div>
    </div>

    <div v-if="rowActionButtons.length && showActionButtons" ref="ActionButtons" class="ag-cell-name-renderer__actions">
      <template v-for="actionButton in rowActionButtons">
        <Btn
          :class="`ag-cell-name-renderer__${actionButton.key}`"
          :data-button-name="`name-renderer__${actionButton.key}`"
          :key="actionButton.key"
          v-if="actionButton.shouldShow"
          color="white"
          :icon="!actionButton.text"
          :to="actionButton.to"
          :data-test-grid-row-action="actionButton.key"
          @click="actionButton.clickAction"
        >
          <component :is="actionButton.icon" />
          <template v-if="actionButton.text">{{ actionButton.text }}</template>
        </Btn>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, PropType } from 'vue';
import FolderFilledIcon16 from '@components/Icons/FolderFilledIcon16.vue';
import SidebarIcon16 from '@components/Icons/SidebarIcon16.vue';
import OpenIcon16 from '@components/Icons/OpenIcon16.vue';

import CloseDockIcon16 from '@components/Icons/CloseDockIcon16.vue';
import nameRendererFillPositions, { NameRendererPosition } from '@utils/nameRendererCreatePositions';
import { ProjectCode, ProjectSource, ProjectMemo, ProjectQuotation } from '@/types';
import { ICellRendererParams } from 'ag-grid-community';
import { GridableEntity, GridableFolder, HighlightableName } from '../utils/types';
import firstToUpper from '@common/util/firstToUpper';

type NameRendererEntity = GridableEntity;

type NameRendererCellParams = ICellRendererParams<NameRendererEntity, string | HighlightableName> & {
  entityType: 'report' | 'source' | 'memo' | 'code' | 'quotation';
  isEditable: boolean;
  showDetailsButton: boolean;
  projectId: string;
};
import { updateQuotation } from '@utils/quotation';
import i18n from '@/i18n';
import getFirstErrorCode from '@utils/getFirstErrorCode';
import { getCodeUpdateError } from '@utils/getCodeUpdateError';

export type RowDetailsButtonClickedEvent = CustomEvent<{
  data: ProjectCode | ProjectSource | ProjectMemo | ProjectQuotation;
}>;
export type RowOpenButtonClickedEvent = CustomEvent<{
  data: ProjectCode | ProjectSource | ProjectMemo | ProjectQuotation;
}>;

export default defineComponent({
  name: 'NameRenderer',

  components: {
    FolderFilledIcon16,
    CloseDockIcon16,
    SidebarIcon16,
    OpenIcon16,
  },

  props: {
    params: {
      type: Object as PropType<NameRendererCellParams>,
      required: true,
    },
  },

  data(): {
    showActionButtons: boolean;
    entityShownInSidebar: boolean;
    isEditModeActive: boolean;
    inputValue: string;
    rightInputPadding: number;
    isColumnWrapped: boolean;
    colId: string;
  } {
    return {
      isEditModeActive: false,
      showActionButtons: false,
      entityShownInSidebar: false,
      inputValue: '',
      rightInputPadding: 0,
      isColumnWrapped: true,
      colId: '',
    };
  },

  computed: {
    /**
     * determines if text content in NameRenderer should get highlighted
     */
    isHighlightedContent(): boolean {
      return this.params.data?.nameWithHighlights?.highlightingPositions.length > 0;
    },

    highlightContent(): Array<NameRendererPosition> {
      if (!this.isHighlightedContent) {
        return [];
      }

      const text: string = this.nameString;

      const positions = this.params.data.nameWithHighlights.highlightingPositions;

      return nameRendererFillPositions(text, positions);
    },

    hasName(): boolean {
      return Boolean(this.nameString);
    },

    namePlaceholder(): string {
      return this.$t(`${this.entityType}.titlePlaceholder`);
    },

    nameString(): string {
      return typeof this.params.value === 'string' ? this.params.value : this.params.value.text;
    },

    /**
     * the base entity type of the grid (not neccessarily the entity type in the row)
     */
    entityType(): string {
      return this.params.entityType;
    },

    /**
     * The number of non-folder children of a Folder node or child codes of a category code
     */
    childCount(): number {
      const entity = this.params.node.data;

      return entity.__typename === 'Folder' || (entity.__typename === 'Code' && entity.isCategory === true)
        ? entity.entityCount
        : 0;
    },

    childrenIconComponent(): ReturnType<typeof defineAsyncComponent> {
      let childEntityType = firstToUpper(this.entityType);

      switch (this.entityType) {
        case 'source':
          childEntityType = 'Document';
          break;

        default:
          break;
      }

      return defineAsyncComponent(() => import(`@components/Icons/${childEntityType}Icon16.vue`));
    },
    isFolder(): boolean {
      return this.params.data?.__typename === 'Folder';
    },

    hasChildren(): boolean {
      return this.params.node.allChildrenCount > 0;
    },

    rowActionButtons() {
      if (!this.params.data) {
        return [];
      }

      // don't show anything  if ...
      // - quotation is a range only, i.e. not been created (concept analysis report)
      // - node is a folder
      if (('isRange' in this.params.data && this.params.data.isRange) || this.isFolder) {
        return [];
      }

      /**
       * Route params for the open button. Should always render as a anchor tag
       */
      const routeParams =
        this.entityType === 'quotation' && this.params.data.__typename === 'Quotation'
          ? {
              name: 'source',
              params: { sourceId: this.params.data.source?.id, projectId: this.params.projectId },
              query: { quotation: this.params.data.id },
            }
          : {
              name: this.entityType,
              params: { [`${this.entityType}Id`]: this.params.data.id, projectId: this.params.projectId },
            };

      const actionButtons: Array<{
        key: string;
        tooltip: string;
        text: string;
        icon: string;
        shouldShow: boolean;
        to?: {
          name: string;
          params?: Record<string, string>;
          query?: Record<string, string>;
        };
        clickAction: (event: Event) => void;
      }> = [
        {
          key: 'open',
          tooltip: this.$t('action.open'),
          text: this.$t('action.open'),
          icon: 'OpenIcon16',
          shouldShow: ['source', 'memo', 'quotation'].includes(this.entityType),
          to: routeParams,
          clickAction: () => {
            const event = new CustomEvent<RowOpenButtonClickedEvent['detail']>('rowOpenButtonClicked', {
              bubbles: true,
              detail: {
                data: this.params.data as Exclude<NameRendererEntity, GridableFolder>,
              },
            });

            this.$el.dispatchEvent(event);
          },
        },
      ];

      if (this.params.showDetailsButton) {
        actionButtons.push({
          key: 'details',
          tooltip: this.$t('dock.details'),
          text: this.$t(this.entityShownInSidebar ? 'action.close' : 'dock.details'),
          icon: this.entityShownInSidebar ? 'CloseDockIcon16' : 'SidebarIcon16',
          shouldShow: true,
          clickAction: () => {
            const event = new CustomEvent<RowDetailsButtonClickedEvent['detail']>('rowDetailsButtonClicked', {
              bubbles: true,
              detail: {
                data: this.params.data as Exclude<NameRendererEntity, GridableFolder>,
              },
            });

            this.$el.dispatchEvent(event);
          },
        });
      }

      return actionButtons;
    },

    hasChanges(): boolean {
      return this.inputValue !== this.nameString;
    },
  },

  activated() {
    this.showActionButtons = false;
  },

  methods: {
    toggleEditMode(): void {
      if (this.params.isEditable) {
        this.isEditModeActive = !this.isEditModeActive;
      }
    },

    stopEventPropagation(event: KeyboardEvent | MouseEvent) {
      event.stopPropagation();
    },

    cancelEditing() {
      this.inputValue = this.nameString;

      this.isEditModeActive = false;
    },

    async updateEntity(newNameValue: string) {
      if (this.entityType === 'quotation') {
        return updateQuotation({ name: newNameValue, id: this.params.data.id });
      }

      const result = await this.$store
        .dispatch(`${this.entityType}/update`, {
          id: this.params.data.id,
          name: newNameValue,
        })
        .catch((error) => {
          const message = getCodeUpdateError(getFirstErrorCode(error));

          if (message) {
            this.$store.dispatch('notification/add', {
              message,
            });
          } else {
            throw error;
          }
        });

      return result;
    },

    async handleBlur() {
      this.isEditModeActive = false;
      if (this.hasChanges) {
        const oldName = this.nameString;
        const translatedEntityName = this.$t(`entity.${this.entityType}`, 1);
        const result = await this.updateEntity(this.inputValue);

        if (result) {
          await this.$store.dispatch('notification/add', {
            message: i18n.global.t('notification.entityNameChanged', {
              entity: translatedEntityName,
            }),
            undo: () => this.updateEntity(oldName),
          });
        }
      }
    },
    triggerShowActionButtons() {
      if (!this.isEditModeActive) {
        // don't show when user is editing name
        this.showActionButtons = true;
      }
    },
    triggerHideActionButtons() {
      this.showActionButtons = false;
    },
  },

  created() {
    this.colId = `${this.params.column.getColDef().cellRendererParams.entityType}.name`;
    this.$watch(
      () => this.$store.state.gridWrap.wrappedColumns[this.colId],
      () => {
        this.isColumnWrapped = this.$store.getters['gridWrap/isColumnWrapped'](this.colId);
      },
      { immediate: true }
    );
  },

  mounted() {
    // Prevent the default grid key behavior, because we have our own
    this.$el.addEventListener('keydown', this.stopEventPropagation);
    this.inputValue = this.nameString;

    if (this.rowActionButtons.length) {
      this.params.node.addEventListener('mouseEnter', this.triggerShowActionButtons);
      this.params.node.addEventListener('mouseLeave', this.triggerHideActionButtons);
    }
  },

  beforeUnmount() {
    this.params.node.removeEventListener('mouseEnter', this.triggerShowActionButtons);
    this.params.node.removeEventListener('mouseLeave', this.triggerHideActionButtons);
  },

  watch: {
    /**
     * Get the width of the action buttons to reduce the width of the input accordingly
     */
    showActionButtons(newVal) {
      this.$nextTick(() => {
        if (newVal && !this.isColumnWrapped) {
          this.rightInputPadding =
            (this.$refs.ActionButtons as Element).clientWidth +
            // right padding
            24;
        } else if (this.isEditModeActive) {
          // when action buttons are not shown,
          // need small padding to prevent focus state from
          // being cut off
          this.rightInputPadding = 8;
        } else {
          this.rightInputPadding = 0;
        }
      });
    },
    // without this watcher the action buttons disappear when the user clicks on the details button
    '$route.params': {
      immediate: true,
      handler() {
        if (this.$route.params[`${this.entityType}Id`] === this.params.data?.id) {
          this.entityShownInSidebar = true;

          return this.triggerShowActionButtons();
        }

        return (this.entityShownInSidebar = false);
      },
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'mixins/cell-gradient';
@use 'mixins/typography';

@include cell-gradient.cell-gradient;

.ag-cell-name-renderer {
  display: flex;
  flex: 1;
  grid-template-columns: auto minmax(0, 1fr) auto;
  position: relative;
}

.ag-cell-name-renderer__group-icon {
  flex: 0 0 auto;
  margin-right: 8px;
  display: flex;
  align-items: center;
}

.ag-cell-name-renderer__wrapper {
  display: flex;
  width: 100%;

  &--name-editing {
    padding-top: 11px; // align with action buttons
    padding-left: 4px;

    .ag-cell-name-renderer__name {
      padding-top: 0;
    }
  }
}

.ag-cell-name-renderer__name {
  padding-top: 15px;
  padding-bottom: 15px;
  color: colors.$color-grey-900;
  font-weight: var(--ag-cell-name-renderer__name__font-weight);
  flex-grow: 1;
  width: calc(100% - 16px);
  max-width: calc(100% - 16px);
  word-break: break-word;
  position: relative;

  @include typography.typography-14-medium(24px);

  &--is-editable {
    cursor: text;

    .ProseMirror {
      // align with action buttons
      padding-top: 5px;
      padding-bottom: 5px;
    }
  }
}

.ag-cell-name-renderer__child-count {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin: 0 16px;
  color: colors.$color-grey-400;
  @include typography.typography-14-normal;
  // we need to push the text a little bit from the bottom so that it's centered with the icon
  span {
    padding-bottom: 2px;
  }
}

.ag-cell-name-renderer__highlight {
  background: colors.$editor-range-active;
}

.ag-cell-name-renderer__input-field--wrapper {
  margin-top: 11px;
  padding: 0 4px;
  flex: 1;
}

.ag-cell-name-renderer__actions {
  margin-top: 11px;
  position: absolute;
  display: flex;
  gap: 3px;
  // position right 17px to respect padding
  right: 17px;

  a {
    display: inline-block;
  }
}

.ag-cell-name-renderer__wrapped-column {
  white-space: normal;
  word-break: break-word;
}

.ag-cell-name-renderer__placeholder {
  color: colors.$color-grey-500;
}
</style>
